import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../config';
import { accessToken } from '../configuration/tokenHandling/accessToken';
import { AccessToken } from '../configuration/tokenHandling/tokenSlice';
import { VinBlacklistModel } from '../features/app/context/connectivity-interruption/components/InterruptionVinUpload';

const REQUEST_MAPPING = '/vin-blacklist';
const TAG = 'VinBlacklist';

export const prepareHeaders = (headers: Headers) => {
    const token = accessToken.getAccessToken() as AccessToken;
    if (token) {
        headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
};

export const connectivityInterruptionApi = createApi({
    reducerPath: 'vinBlacklistApi',
    tagTypes: [TAG],
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.DEVICE_STATE_SERVICE,
        prepareHeaders,
    }),
    endpoints: (builder) => ({
        // Upload CSV
        uploadVinList: builder.mutation<any, { file: File; vinBlacklistModel: VinBlacklistModel }>({
            query: ({ file, vinBlacklistModel }) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('vinBlacklistModel', new Blob([JSON.stringify(vinBlacklistModel)],
                { type: 'application/json' }));

                return {
                    url: `${REQUEST_MAPPING}/upload-list`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: [TAG],
        }),
        // Change VIN list state (BLACKLIST/WHITELIST)
        changeVinListState: builder.mutation<void, VinBlacklistModel>({
            query: (vinBlacklistModel) => ({
                url: `${REQUEST_MAPPING}/change-state`,
                method: 'POST',
                body: vinBlacklistModel,
            }),
            invalidatesTags: [TAG],
        }),
        // Get all VIN lists
        getAllVinLists: builder.query<VinBlacklistModel[], void>({
            query: () => `${REQUEST_MAPPING}/all-lists`,
            providesTags: [TAG],
        }),
        // Get all logs
        getAllLogs: builder.query<VinBlacklistModel[], void>({
            query: () => `${REQUEST_MAPPING}/all-logs`,
            providesTags: [TAG],
        }),
        // Download CSV file by filename
        getCsvFile: builder.query<Blob, string>({
            query: (filename) => ({
                url: `${REQUEST_MAPPING}/get-file/${filename}`,
                method: 'GET',
                responseHandler: (response) => response.blob(),
            }),
        }),
    }),
});

export const {
    useUploadVinListMutation,
    useChangeVinListStateMutation,
    useGetAllVinListsQuery,
    useGetAllLogsQuery,
    useGetCsvFileQuery,
    useLazyGetCsvFileQuery
} = connectivityInterruptionApi;